import { React, useState, useEffect, createContext, useContext, useRef } from 'react';
import { HddStack, Envelope, Slack, MicrosoftTeams, Tools, ShieldCheck } from 'react-bootstrap-icons';

import ConfigService from '../../services/config.service';
import ProductSettingsService from '../../services/product.service';
import { AuthContext } from '../../services/auth.service';
import LoadingScreen from '../../components/elements/LoadingScreen';

import "./styles.scss";

import PageHeader from '../../components/elements/PageHeader';
import { SideMenu } from '../../components/layouts/MainLayout/SideMenu';
import { InteractivePanel } from "../../components/layouts/MainLayout/InteractivePanel";
import { EmailsNotifModal } from '../../components/layouts/MainLayout/SideMenu/EmailNotifModal';
import { SlackNotifModal } from '../../components/layouts/MainLayout/SideMenu/SlackNotifModal';
import { TeamsNotifModal } from '../../components/layouts/MainLayout/SideMenu/TeamsNotifModal';

import {
    ProductSettingsModal,
    CSTSettingsModal,
    SSTSettingsModal,
    TagInactivitySettingsModal,
    CloudMonitoringSettingsModal
} from "./configModals";

import { useMessage } from "../../components/elements/MessageCenter";

const _ = require('lodash');

// Save function
export const SaveConfigData = async (params) => {
    const {
        companyId,
        currentConfig,
        setCurrentConfig,
        configParams,
        modalSaveFunction,
        handlePanelClick,
        setLoadingActive,
        success,
        error,
    } = params;

    setLoadingActive(true);

    const configObj = { ...currentConfig };

    Object.keys(configParams).forEach(key => {
        configObj[key] = configParams[key];
    });

    const resp = await modalSaveFunction({ configObj, companyId });
    const respData = await resp?.json() || resp;

    switch (respData?.result) {
        case true:
            success("Configuration data successfully saved!");
            setCurrentConfig(configObj);
            break;
        default:
            error();
    }

    handlePanelClick("empty");
    setLoadingActive(false);
}

export const ProductSettingsContext = createContext();
export const CSTSettingsContext = createContext();
export const SSTSettingsContext = createContext();
export const TagInactivitySettingsContext = createContext();
export const cloudRunMonitoringContext = createContext();
export const EmailsNotifContext = createContext();
export const SlackNotifContext = createContext();
export const TeamsNotifContext = createContext();

const productDescLink = `https://docs.code-cube.io/tag-monitor/overview/`;
const CSImplmenetationLink = `https://docs.code-cube.io/getting-started/setup-tag-monitor-clientside/`;
const SSImplmenetationLink = `https://docs.code-cube.io/getting-started/setup-tag-monitor-serverside/`;
const TIImplmenetationLink = `https://docs.code-cube.io/tag-monitor/configuration/tag-volume/`;
const clientSideTemplateLink = `https://storage.googleapis.com/portal_dev_bucket/gtm_templates/Code%20Cube%20-%20Client-side%20Tag%20Monitor%20template.tpl`;
const serverSideTemplateLink = `https://storage.googleapis.com/portal_dev_bucket/gtm_templates/Code%20Cube%20-%20Server-side%20Tag%20Monitor%20template.tpl`;
const emailNotifImplementationLink = `https://docs.code-cube.io/notifications/enable-email-notifications/`;
const slackNotifImplementationLink = `https://docs.code-cube.io/notifications/enable-slack-notifications/`;
const teamsNotifImplementationLink = `https://docs.code-cube.io/notifications/enable-teams-notifications/`;

export const TagMonitorConfig = () => {
    const { companyId, companyInfo } = useContext(AuthContext);
    const [currCompanyInfo, setCurrCompanyInfo] = useState(companyInfo);
    const [modalSaveStatus, setModalSaveStatus] = useState(false);

    // Using popup messages
    const { success, warning, error } = useMessage();

    const [sideMenuState, setSideMenuState] = useState(false);
    const [currentModal, setCurrentModal] = useState(<></>);

    const [loadingActive, setLoadingActive] = useState(true);

    const [currentConfig, setCurrentConfig] = useState({});
    const [configParams, setConfigParams] = useState({});

    const [clientSideAvailable, setClientSideAvailable] = useState(false);
    const [serverSideAvailable, setServerSideAvailable] = useState(false);
    const [tagInactivityAvailable, setTagInactivityAvailable] = useState(false);
    const [emailNotifAvailable, setEmailNotifAvailable] = useState(false);
    const [slackNotifAvailable, setSlackNotifAvailable] = useState(false);
    const [teamsNotifAvailable, setTeamsNotifAvailable] = useState(false);

    const [timezones, setTimezones] = useState([]);
    const [databaseName, setDatabaseName] = useState("");

    const [customNmbrCid, setCustomNmbrCid] = useState("");
    const [subscriptionModel, setSubscriptionModel] = useState("");
    const [cloudRunData, setCloudRunData] = useState({});

    const [slackWebhook, setSlackWebhook] = useState("");



    // Reloading the page content only if the new company profile is different
    useEffect(() => _.isEqual(companyInfo, currCompanyInfo) ? undefined : setCurrCompanyInfo(companyInfo), [companyInfo]);

    useEffect(() => {
        (async () => {
            setLoadingActive(true);

            const resp = await ConfigService.getTagMonitorConfigData(companyId);
            const respData = await resp.json();
            
            const cloudRunData = await ConfigService.getCloudRunMonitoringData(companyId);
            const cloudRunDataResponseData = await cloudRunData.json();
            setCloudRunData(cloudRunDataResponseData || {});

            // Product settings data
            setTimezones(respData?.config_data?.timezones || []);

            setDatabaseName(respData?.config_data?.current_config?.database_name);
            setLoadingActive(true);
            setSideMenuState(false);

            setCurrentConfig(respData?.config_data?.current_config);

            const configData = respData?.config_data || {};
            setSlackWebhook(configData?.slack_webhook || "");

            // Product settings data
            setTimezones(respData?.config_data?.timezones || []);

            setDatabaseName(respData?.config_data?.current_config?.database_name);

            setCustomNmbrCid(respData?.config_data?.current_config.custom_nmbr_cid);
            setSubscriptionModel(respData?.config_data?.current_config.subscription_package);

            const subLevel = companyInfo?.tag_monitor_subscription_model?.toLowerCase();
            const settings = await ProductSettingsService.getProductSettings();
            const subSettings = settings?.tag_monitor_settings[subLevel];
            setClientSideAvailable(subSettings?.client_side_monitoring);
            setServerSideAvailable(subSettings?.server_side_monitoring);
            setTagInactivityAvailable(subSettings?.tag_inactivity);
            setEmailNotifAvailable(subSettings?.email_notif);
            setSlackNotifAvailable(subSettings?.slack_notif);
            setTeamsNotifAvailable(subSettings?.teams_notif);

            setCurrentModal(<></>);
            setLoadingActive(false);
        })();
    }, [currCompanyInfo]);

    // const discardUnsavedChanges = () => {
    //     setConfigParams({});        // clear the diff object
        
    //     // restore the original data snapshot
    //     // if (pristineConfigRef.current) {
    //     //   setCurrentConfig(pristineConfigRef.current);
    //     // }
    //     //setModalSaveStatus(false);  // grey-out the Save btn again
    // };

    const modalFunc = {
        "empty": <></>,
        "general":
            <ProductSettingsContext.Provider value={{
                productDescLink,
                timezones,
                timezoneName: currentConfig?.timezone_name,
                curTimezone: currentConfig?.timezone,
                companyDomains: currentConfig?.company_domains,
                setConfigParams,
                setModalSaveStatus,
            }}>
                <ProductSettingsModal context={ProductSettingsContext} />
            </ProductSettingsContext.Provider>,
        "cst_monitoring":
            <CSTSettingsContext.Provider value={{
                databaseName,
                CSImplmenetationLink,
                clientSideTemplateLink,
                clientErrorsThreshold: currentConfig?.client_errors_threshold,
                gtmContainers: currentConfig?.gtm_containers_client || [],
                tags_monitoring_exclude_client: currentConfig?.tags_monitoring_exclude_client,
                setConfigParams,
                customNmbrCid,
                subscriptionModel,
                setModalSaveStatus,
            }}>
                <CSTSettingsModal context={CSTSettingsContext} />
            </CSTSettingsContext.Provider>,
        "sst_monitoring":
            <SSTSettingsContext.Provider value={{
                databaseName,
                SSImplmenetationLink,
                serverSideTemplateLink,
                serverErrorsThreshold: currentConfig?.server_errors_threshold,
                gtmContainers: currentConfig?.gtm_containers_server || [],
                tags_monitoring_exclude_server: currentConfig?.tags_monitoring_exclude_server,
                setConfigParams,
                serverSideAvailable,
                customNmbrCid,
                subscriptionModel,
                setModalSaveStatus,
            }}>
                <SSTSettingsModal context={SSTSettingsContext} />
            </SSTSettingsContext.Provider>,
        "tag_inactivity":
            <TagInactivitySettingsContext.Provider value={{
                TIImplmenetationLink,
                companyDomains: currentConfig?.company_domains,
                gtmContainers: {
                    client: currentConfig?.gtm_containers_client || [],
                    server: currentConfig?.gtm_containers_server || [],
                },
                checkFrequency: currentConfig?.check_frequency,
                tagsInactivityClientSections:currentConfig?.tags_inactivity_client?.config_sections || [],
                tagsInactivityServerSections:currentConfig?.tags_inactivity_server?.config_sections || [],
                setConfigParams,
                setModalSaveStatus,
            }}>
                <TagInactivitySettingsModal context={TagInactivitySettingsContext} />
            </TagInactivitySettingsContext.Provider>,

        "cloud_run":
            <cloudRunMonitoringContext.Provider value={{
                companyId,
                cloudRunData,
                companyInfo,
                setModalSaveStatus,
            }}>
                <CloudMonitoringSettingsModal context={cloudRunMonitoringContext} />
            </cloudRunMonitoringContext.Provider>,

        "email_notif":
            <EmailsNotifContext.Provider value={{
                emailNotifImplementationLink,
                enableEmailNotif: currentConfig?.email_updates || false,
                curEmailsList: currentConfig?.emails?.split(";") || [''],
                setConfigParams,
                setModalSaveStatus,
            }}>
                <EmailsNotifModal context={EmailsNotifContext} />
            </EmailsNotifContext.Provider>,
        "slack_notif":
            <SlackNotifContext.Provider value={{
                slackNotifImplementationLink,
                enableSlackNotif: currentConfig?.slack_updates || false,
                slackChannel: currentConfig?.slack_channel || "",
                slackWorkspaceId: currentConfig?.slack_workspace_id || "",
                slackWebhook: slackWebhook || "",
                setConfigParams,
                setModalSaveStatus,
            }}>
                <SlackNotifModal context={SlackNotifContext} />
            </SlackNotifContext.Provider>,
        "teams_notif":
            <TeamsNotifContext.Provider value={{
                teamsNotifImplementationLink,
                enableTeamsNotif: currentConfig?.teams_updates || false,
                teamsChannel: currentConfig?.teams_channel || "",
                teamsWebhook: currentConfig?.teams_webhook || "",
                setConfigParams,
                setModalSaveStatus,
            }}>
                <TeamsNotifModal context={TeamsNotifContext} />
            </TeamsNotifContext.Provider>,
    }

    const handlePanelClick = (page) => {

        setSideMenuState(!sideMenuState);
        setCurrentModal(modalFunc[page]);
    }

    return (
        <>
            <SideMenu
                globalState={sideMenuState}
                setGlobalState={setSideMenuState}
                modalContent={currentModal}
                popoverMessage={<>No changes to save.</>}
                saveFunction={() => SaveConfigData({
                    companyId,
                    currentConfig,
                    setCurrentConfig,
                    configParams,
                    modalSaveFunction: ConfigService.saveTagMonitorConfigData,
                    handlePanelClick,
                    setLoadingActive,
                    success,
                    error,
                    warning,
                    setModalSaveStatus,
                })}
                saveStatus={modalSaveStatus}
                confirmationNeeded={true}
            />
            <div className="row">
                <LoadingScreen isActive={loadingActive} />
                <div className="col-12 col-sm-10 col-md-8 text-start">
                    <div>
                        <div className="row">
                            <PageHeader categoryName="configuration" pageName="Tag Monitor" />
                            <div className="container-rows mt-4">
                                <div>
                                    <InteractivePanel
                                        icon={
                                            <svg width="36" height="36" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="19.084" cy="18.8315" r="8.81877" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M13.2442 9.92664C13.1578 9.67701 13.2788 9.40304 13.5215 9.2988L15.4641 8.4645C15.696 8.36491 15.9658 8.4522 16.0954 8.66875L16.7805 9.81351L13.6212 11.0161L13.2442 9.92664Z" fill="#C78FFF"/>
                                            <circle cx="18.701" cy="18.8962" r="8.81877" transform="rotate(32.5681 18.701 18.8962)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M18.2164 8.1938C18.2521 7.93206 18.4838 7.74226 18.7474 7.75885L20.8574 7.89163C21.1093 7.90748 21.3106 8.10719 21.3284 8.35892L21.4228 9.68969L18.0609 9.33617L18.2164 8.1938Z" fill="#C78FFF"/>
                                            <circle cx="18.6062" cy="18.7905" r="8.81877" transform="rotate(63.5775 18.6062 18.7905)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M23.68 9.24714C23.8603 9.05408 24.159 9.033 24.3646 9.19884L26.0103 10.5261C26.2067 10.6846 26.2557 10.9639 26.1248 11.1797L25.4331 12.3204L22.8931 10.0898L23.68 9.24714Z" fill="#C78FFF"/>
                                            <circle cx="18.9423" cy="18.7066" r="8.81877" transform="rotate(92.0599 18.9423 18.7066)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M27.637 12.9186C27.8806 12.8164 28.1617 12.9197 28.2813 13.1553L29.238 15.0406C29.3523 15.2656 29.2824 15.5404 29.0746 15.6836L27.9759 16.4405L26.5738 13.3645L27.637 12.9186Z" fill="#C78FFF"/>
                                            <circle cx="18.5756" cy="18.7768" r="8.81877" transform="rotate(125.185 18.5756 18.7768)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M29.1188 18.5309C29.3807 18.5654 29.5715 18.7963 29.5561 19.06L29.4323 21.1706C29.4176 21.4225 29.2187 21.6247 28.9671 21.6436L27.6367 21.7437L27.9758 18.3803L29.1188 18.5309Z" fill="#C78FFF"/>
                                            <circle cx="18.7435" cy="18.4266" r="8.81877" transform="rotate(157.753 18.7435 18.4266)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M27.7249 23.8976C27.9268 24.0679 27.9631 24.3652 27.8079 24.579L26.566 26.29C26.4177 26.4942 26.1413 26.5573 25.9191 26.4376L24.7446 25.8047L26.8433 23.1546L27.7249 23.8976Z" fill="#C78FFF"/>
                                            <circle cx="18.8845" cy="18.4099" r="8.81877" transform="rotate(-171.238 18.8845 18.4099)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M23.9654 27.8133C24.0385 28.0671 23.9031 28.3343 23.6552 28.4255L21.6711 29.1557C21.4343 29.2429 21.1695 29.1414 21.0515 28.9183L20.4281 27.7388L23.6466 26.7053L23.9654 27.8133Z" fill="#C78FFF"/>
                                            <circle cx="18.7594" cy="18.733" r="8.81877" transform="rotate(-142.755 18.7594 18.733)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M18.5276 29.1167C18.4708 29.3746 18.2244 29.5449 17.963 29.5069L15.8708 29.2025C15.6211 29.1662 15.4367 28.9507 15.4394 28.6984L15.4539 27.3643L18.7758 27.9908L18.5276 29.1167Z" fill="#C78FFF"/>
                                            <circle cx="18.878" cy="18.3864" r="8.81877" transform="rotate(-116.774 18.878 18.3864)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <circle cx="19.1082" cy="18.6992" r="8.81877" transform="rotate(-84.2059 19.1082 18.6992)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M9.64457 24.1058C9.3879 24.1682 9.12659 24.0219 9.04573 23.7704L8.39861 21.7577C8.32136 21.5174 8.43375 21.2571 8.66156 21.1485L9.86591 20.5746L10.7648 23.8333L9.64457 24.1058Z" fill="#C78FFF"/>
                                            <circle cx="19.0566" cy="18.8314" r="8.81877" transform="rotate(-53.1965 19.0566 18.8314)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M8.39719 18.5824C8.14555 18.5021 7.99865 18.241 8.06055 17.9842L8.556 15.9289C8.61514 15.6836 8.84663 15.5198 9.09766 15.5457L10.4247 15.6828L9.49548 18.933L8.39719 18.5824Z" fill="#C78FFF"/>
                                            <circle cx="18.8303" cy="18.5692" r="8.81877" transform="rotate(-24.7141 18.8303 18.5692)" stroke="currentColor" strokeWidth="1.51179"/>
                                            <path d="M9.6951 13.5698C9.50671 13.3847 9.49328 13.0854 9.66432 12.8841L11.0333 11.273C11.1967 11.0807 11.4772 11.0389 11.6895 11.1752L12.8122 11.8959L10.5173 14.378L9.6951 13.5698Z" fill="#C78FFF"/>
                                            <rect x="15.6782" y="20.2761" width="3.12115" height="11.5175" rx="1.56057" transform="rotate(39.1635 15.6782 20.2761)" fill="#C78FFF" stroke="currentColor" strokeWidth="0.408355"/>
                                            <path d="M18.0619 15.3727C17.9075 14.794 17.3046 14.4399 16.7741 14.718C16.2837 14.9752 15.8382 15.3151 15.4585 15.7254C14.8319 16.4025 14.41 17.2434 14.2418 18.1506C14.0737 19.0577 14.1661 19.994 14.5085 20.8507C14.8508 21.7075 15.429 22.4496 16.1761 22.9911C16.9231 23.5325 17.8083 23.8511 18.729 23.9099C19.6497 23.9687 20.5683 23.7653 21.3781 23.3232C22.1879 22.8812 22.8558 22.2186 23.3043 21.4124C23.5761 20.9238 23.7606 20.3947 23.8524 19.8486C23.9516 19.258 23.4275 18.7951 22.8295 18.8285L22.1879 18.8643C21.5899 18.8977 21.1559 19.4397 20.8855 19.974C20.8733 19.9981 20.8606 20.0219 20.8475 20.0456C20.6574 20.3873 20.3743 20.6681 20.031 20.8555C19.6878 21.0429 19.2984 21.1291 18.9082 21.1042C18.5179 21.0793 18.1427 20.9442 17.826 20.7147C17.5094 20.4852 17.2643 20.1706 17.1192 19.8075C16.9741 19.4443 16.9349 19.0475 17.0062 18.663C17.0775 18.2785 17.2563 17.922 17.5219 17.635C17.5403 17.6152 17.559 17.5957 17.5781 17.5767C18.0018 17.1534 18.3818 16.5723 18.2275 15.9936L18.0619 15.3727Z" fill="#C78FFF"/>
                                            </svg>
                                        }
                                        headerText="General settings"
                                        descriptionText="Settings for standard configuration like domain(s) to monitor and your time zone"
                                        available={true}
                                        clickFunction={() => handlePanelClick("general")}

                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon = {
                                            <svg width="21" height="21" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6.55556 21H20.3333C20.7016 21 21 20.7016 21 20.3333V1.66667C21 1.29848 20.7016 1 20.3333 1H1.66667C1.29848 1 1 1.29848 1 1.66667V15.4444" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M8.77734 4.33301H17.6662" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M4.33398 4.33301H5.4451" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M1.55469 20.4444L10.9991 11M10.9991 11V15.4444M10.9991 11H6.55469" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        }
                                        headerText="Client-side error monitoring"
                                        descriptionText="Settings for monitoring of the client-side tag manager container"
                                        available={clientSideAvailable}
                                        clickFunction={() => handlePanelClick("cst_monitoring")}

                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon={<HddStack size={21} />}
                                        headerText="Server-side error monitoring"
                                        descriptionText="Settings for monitoring of the server-side tag manager container"
                                        available={serverSideAvailable}
                                        clickFunction={() => handlePanelClick("sst_monitoring")}
                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon={<ShieldCheck size={21} />}
                                        headerText="Cloud Run monitoring"
                                        descriptionText="Settings for health monitoring of the server-side tagging instance in Cloud Run (GCP)"
                                        available={serverSideAvailable}
                                        clickFunction={() => handlePanelClick("cloud_run")}
                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon = {
                                            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9 2H5.8C4.11984 2 3.27976 2 2.63803 2.32698C2.07354 2.6146 1.6146 3.07354 1.32698 3.63803C1 4.27976 1 5.11984 1 6.8V15.2C1 16.8802 1 17.7202 1.32698 18.362C1.6146 18.9265 2.07354 19.3854 2.63803 19.673C3.27976 20 4.11984 20 5.8 20H14.2C15.8802 20 16.7202 20 17.362 19.673C17.9265 19.3854 18.3854 18.9265 18.673 18.362C19 17.7202 19 16.8802 19 15.2V12M10 7H14V11M13.5 2.5V1M17.4393 3.56066L18.5 2.5M18.5103 7.5H20.0103M1 12.3471C1.65194 12.4478 2.31987 12.5 3 12.5C7.38636 12.5 11.2653 10.3276 13.6197 7" stroke="#3100C4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>}
                                        headerText="Tag Volume monitoring"
                                        descriptionText="Settings for monitoring of tag volume in real-time"
                                        available={tagInactivityAvailable}
                                        clickFunction={() => handlePanelClick("tag_inactivity")}
                                    />
                                </div>
                            </div>
                            <h3 style={{ fontSize: '1em' }} className="page-category">Notification settings</h3>
                            <div className="container-rows">
                                <div>
                                    <InteractivePanel
                                        icon={
                                            <svg width="29" height="23" viewBox="0 0 29 23" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#C78FFF' }}>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M27.968 13.0457L16.0391 0.549338C15.9538 0.459976 15.8519 0.395966 15.7429 0.357527L16.4113 12.5273L27.968 13.0457ZM15.7428 13.4342L15.5218 13.5274L15.2737 9.01009L7.67345 8.83352L19.8475 21.5868C19.9923 21.7385 20.1849 21.8172 20.3792 21.8217L20.5691 13.6508L15.7428 13.4342ZM21.5683 13.6956L21.3949 21.1596L28.3373 14.5325C28.4867 14.3899 28.5652 14.201 28.5719 14.0098L21.5683 13.6956ZM7.38776 7.82662L15.2187 8.00854L14.8155 0.667481L7.54934 7.60364C7.48032 7.66952 7.42643 7.74529 7.38776 7.82662Z" fill="currentColor"/>
                                            <line x1="0.284792" y1="6.79077" x2="1.99354" y2="6.79077" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="3.32257" y1="6.79077" x2="5.03132" y2="6.79077" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="0.284792" y1="11.7272" x2="3.51243" y2="11.7272" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="4.46173" y1="11.7272" x2="4.65159" y2="11.7272" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="3.32257" y1="14.3853" x2="6.55021" y2="14.3853" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            <line x1="1.42395" y1="17.423" x2="8.82854" y2="17.423" stroke="currentColor" strokeWidth="0.569584" strokeLinecap="round"/>
                                            </svg>
                                        } 
                                        headerText="Email notifications"
                                        descriptionText="Settings to configure email notifications"
                                        available={emailNotifAvailable}
                                        clickFunction={() => handlePanelClick("email_notif")}
                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon={<Slack size={21} />}
                                        headerText="Slack notifications"
                                        descriptionText="Settings to configure Slack notifications"
                                        available={slackNotifAvailable}
                                        clickFunction={() => handlePanelClick("slack_notif")}
                                    />
                                </div>
                                <div>
                                    <InteractivePanel
                                        icon={<MicrosoftTeams size={21} />}
                                        headerText="Teams notifications"
                                        descriptionText="Settings to configure Teams notifications"
                                        available={teamsNotifAvailable}
                                        clickFunction={() => handlePanelClick("teams_notif")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
